import React, {useEffect, useState} from "react";
import {create} from "simple-drawing-board";

export const Chat = ({isControl, room, placeholder, topic, receiveEvents}) => {
    if (isControl) return <ChatDisplay room={room} receiveEvents={receiveEvents} />
    else return <ChatPlayer placeholder={placeholder} topic={topic} room={room} />;
}

export const DrawingChat = ({isControl, room, receiveEvents}) => {
    if (isControl) return <DrawingChatDisplay room={room} receiveEvents={receiveEvents}/>
    else return <DrawingChatPlayer room={room} />;
}

const AVATAR_FILTER = 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)';
const avatarStyle = (size) => ({
    width: size,
    height: size,
    marginLeft: 8,
    marginRight: 8,
    objectFit: 'cover',
    objectPosition: 'top',
    filter: AVATAR_FILTER,
    willChange: 'filter',
});

function isCanvasBlank(canvas) {
    const context = canvas.getContext('2d');
    const pixelBuffer = new Uint32Array(
        context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );

    return !pixelBuffer.some(color => color !== 0);
}

const DRAWING_COLORS = ['#FFFFFF', '#EE204D', '#FFDC00', '#1F75FE', '#B5674D', '#FF7538', '#1CAC78', '#925EAE'];

export const DrawingChatPlayer = ({room}) => {
    const [sdb, setSdb] = useState();
    const [selectedColor, setSelectedColor] = useState('#FFFFFF');

    useEffect(() => {
        const sdb = create(document.getElementById("drawing"));
        sdb.setLineSize(10);
        sdb.setLineColor('white');
        setSdb(sdb);

        return () => {
            sdb.clear();
            sdb.destroy();
        };
    }, []);

    const updateSelectedColor = (color) => {
        setSelectedColor(color);
        if (sdb) sdb.setLineColor(color);
    };

    const sendDrawing = () => {
        const canvas = document.getElementById('drawing');
        if (isCanvasBlank(canvas)) return;
        fetch('/chat/send-chat', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({content: sdb.toDataURL(), room})
        }).then(() => {
            sdb.clear();
        });
    };

    const ghostBtn = {
        flex: 1,
        padding: '12px 0',
        fontSize: 18,
        fontWeight: 700,
        color: 'black',
        backgroundColor: '#ffffff',
        border: '3px solid black',
        borderRadius: 6,
        cursor: 'pointer',
        boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
        fontFamily: 'inherit',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
    };

    return <div style={{
        alignSelf: 'flex-start',
        width: '100%',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: 12,
        color: 'white',
        backgroundColor: '#5a6675',
    }}>
        <canvas
            id="drawing"
            width="500"
            height="500"
            style={{
                width: '100%',
                aspectRatio: '1 / 1',
                height: 'auto',
                backgroundColor: '#0a0a0a',
                border: '3px solid black',
                borderRadius: 6,
                touchAction: 'none',
                boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
            }}
        />

        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 4,
            alignItems: 'center',
            padding: '10px 12px',
            backgroundColor: '#232345',
            border: '3px solid black',
            borderRadius: 6,
            boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
        }}>
            {DRAWING_COLORS.map((color) => {
                const isSelected = selectedColor === color;
                return <div
                    key={color}
                    onClick={() => updateSelectedColor(color)}
                    style={{
                        flex: 1,
                        aspectRatio: '1 / 1',
                        backgroundColor: color,
                        border: isSelected ? '3px solid white' : '3px solid transparent',
                        borderRadius: 4,
                        cursor: 'pointer',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                    }}
                />;
            })}
        </div>

        <div style={{display: 'flex', gap: 12}}>
            <button onClick={() => sdb && sdb.undo()} style={ghostBtn}>↶ Undo</button>
            <button onClick={() => sdb && sdb.clear()} style={ghostBtn}>✕ Clear</button>
        </div>

        <button
            onClick={sendDrawing}
            style={{
                ...ghostBtn,
                flex: 'none',
                padding: '16px 0',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: 4,
                textTransform: 'uppercase',
                backgroundColor: '#000',
                color: 'white',
                border: '3px solid white',
            }}
        >Send</button>
    </div>;
};

const ChatPlayer = ({placeholder, room, topic}) => {
    const [content, setContent] = useState("");
    const [pressed, setPressed] = useState(false);

    const submitAnswer = () => {
        fetch('/chat/send-chat', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({content, room})
        }).then(() => {
            setContent("");
        })
    }

    return <div style={{
        alignSelf: 'flex-start',
        width: '100%',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 16,
        backgroundImage: 'linear-gradient(rgba(0,0,0,0) 0%, rgba(230,41,158,0.55) 55%, rgba(20,5,40,0.95) 100%)',
        color: 'white',
    }}>
        {topic && (
            <div style={{
                width: '100%',
                maxWidth: 200,
                padding: '14px 18px',
                backgroundColor: 'rgba(0,0,0,0.55)',
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 8,
                textAlign: 'center',
                fontSize: 22,
                fontWeight: 800,
                lineHeight: 1.25,
                textShadow: '1px 1px 0 rgba(0,0,0,0.95)',
                marginBottom: 4,
            }}>{topic}</div>
        )}
        <div style={{position: 'relative', width: '100%', maxWidth: 460}}>
            <input
                type="text"
                className="inputbox"
                placeholder={placeholder}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                style={{width: '100%', boxSizing: 'border-box', borderRadius: 12}}
            />
            <span className="focus-border"><i /></span>
        </div>
        <button
            onClick={submitAnswer}
            onPointerDown={() => setPressed(true)}
            onPointerUp={() => setPressed(false)}
            onPointerLeave={() => setPressed(false)}
            onPointerCancel={() => setPressed(false)}
            style={{
                border: '1px solid rgba(255,255,255,0.85)',
                borderRadius: 6,
                padding: '16px 46px',
                fontFamily: 'inherit',
                fontWeight: 700,
                fontStyle: 'italic',
                fontSize: 22,
                letterSpacing: 8,
                textTransform: 'uppercase',
                color: 'white',
                textShadow: '0 1px 0 rgba(0,0,0,0.9), 0 -1px 0 rgba(255,255,255,0.15)',
                background: pressed
                    ? 'linear-gradient(180deg, #050507 0%, #101015 50%, #1a1a20 100%)'
                    : 'linear-gradient(180deg, #3a3a44 0%, #1a1a22 45%, #0a0a10 100%)',
                boxShadow: pressed
                    ? [
                        'inset 0 3px 8px rgba(0,0,0,0.85)',
                        'inset 0 -1px 0 rgba(255,255,255,0.05)',
                        '0 1px 0 rgba(0,0,0,0.8)',
                        '0 0 0 rgba(255,255,255,0)',
                      ].join(', ')
                    : [
                        'inset 0 1px 0 rgba(255,255,255,0.35)',
                        'inset 0 -2px 4px rgba(0,0,0,0.55)',
                        '0 1px 0 rgba(60,60,70,0.9)',
                        '0 2px 0 rgba(50,50,60,0.9)',
                        '0 3px 0 rgba(40,40,50,0.9)',
                        '0 4px 0 rgba(30,30,40,0.9)',
                        '0 5px 0 rgba(20,20,30,0.9)',
                        '0 6px 0 rgba(10,10,20,0.9)',
                        '0 8px 12px rgba(0,0,0,0.6)',
                        '0 0 22px rgba(255,255,255,0.18)',
                      ].join(', '),
                transform: pressed ? 'translateY(6px)' : 'translateY(0)',
                transition: 'transform 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out',
                cursor: 'pointer',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                userSelect: 'none',
            }}>Send</button>
    </div>
}

// The main screen hears every message the server sends; only some are chats. (Their ding comes from the room,
// played by the server as each chat arrives.)
const isChat = (event) => !!event && event.id != null && typeof event.content === 'string';

const DrawingChatDisplay = ({room, receiveEvents}) => {
    const [chats, setChats] = useState([]);

    useEffect(() => {
        fetch(`/chat/get-chats?room=${room}`)
            .then(response => response.json())
            .then((chats) => {
                setChats(chats.slice(-8))
            });
    }, [])

    useEffect(() => {
        return receiveEvents((event) => {
            if (!isChat(event)) return;
            setChats(currentChats => {
                const existingChats = currentChats.length >= 8
                    ? currentChats.slice(-4)
                    : currentChats;
                return [...existingChats, event];
            });
        });
    }, []);

    useEffect(() => {
        const sdbs = [];

        chats.forEach((chat) => {
            const sdb = create(document.getElementById(`drawing-${chat.id}`));
            sdb.clear();
            sdb.fillImageByDataURL(chat.content);

            sdbs.push(sdb);
        })

        return () => {
            sdbs.forEach((sdb) => sdb.destroy());
        };
    }, [chats]);

    console.log(chats);

    return <div style={{
        fontSize: 80,
        flexDirection: 'column',
        width: '1500px',
        height: '900px',
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        justifyContent: 'center',
    }}>
        <div style={{display: 'flex', flexWrap: 'wrap', width: '100%', paddingLeft: 100, paddingTop: 8}}>
            {chats.map((chat, index) =>
                <div key={chat.id} style={{
                    width: '330px',
                    margin: '8px',
                    backgroundColor: 'black',
                    border: '1px solid white',
                }}>
                    <canvas id={`drawing-${chat.id}`} width="330" height="330"/>
                    <div style={{display: 'flex', background: '#313131', padding: 4, paddingLeft: 8, borderTop: '1px solid white'}}>
                        <img src={chat.image_url} alt={chat.name} style={avatarStyle(70)}/>
                        <div style={{color: 'white', paddingLeft: 8}}>
                            <div style={{fontSize: 28}}>{chat.name}</div>
                            <div style={{fontSize: 24, fontWeight: 'bold', width: 240}}>{chat.nickname}</div>
                        </div>
                    </div>
                </div>)
            }
        </div>
    </div>
}

const ChatDisplay = ({room, topic, receiveEvents}) => {
    const [chats, setChats] = useState([]);

    useEffect(() => {
        fetch(`/chat/get-chats?room=${room}`)
            .then(response => response.json())
            .then(setChats);
    }, [])

    useEffect(() => {
        return receiveEvents((event) => {
            if (isChat(event)) setChats((prevChats) => [...prevChats, event]);
        });
    }, []);

    useEffect(() => {
        const chatWindow = document.getElementById('chatWindow');
        chatWindow.scrollTop = chatWindow.scrollHeight;
    }, [chats])

    const renderChats = () => {
        return chats.map((chat) =>
            <div style={{
                margin: 10,
                display: 'flex',
                alignItems: 'center',
                height: 150,
                overflow: 'hidden',
            }}>
                <div style={{paddingLeft: 10, textAlign: 'right'}}>
                    <div style={{fontSize: 48}}>{chat.name}</div>
                    <div style={{fontSize: 32, fontStyle: 'italic', width: 240}}>{chat.nickname}</div>
                </div>
                <img src={chat.image_url} alt={chat.name} style={{
                    ...avatarStyle(230),
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                }}/>
                <div className='bubble bubble-left' style={{ flex: 1, fontSize: 30, margin: 20 }}>
                    {chat.content}
                </div>
            </div>
        )
    }

    return <div style={{display: 'flex', flexDirection: 'column', height: '900px', width: '75%', alignSelf: 'flex-start'}}>
        {topic && (
            <div style={{
                margin: '20px 10px 0',
                padding: '14px 24px',
                fontSize: 44,
                fontWeight: 800,
                color: 'white',
                textAlign: 'center',
                backgroundColor: 'rgba(0,0,0,0.55)',
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 8,
                textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
            }}>{topic}</div>
        )}
        <div id='chatWindow' style={{color: 'white', fontSize: 100, flex: 1, marginTop: 10, marginBottom: 10, border: '0px dashed #a2a2a2', overflowY: 'hidden'}}>
            {renderChats()}
        </div>
    </div>
}
